import { useState } from 'react';
import Modal from '../shared/Modal.jsx';
import { useJobs } from '../../hooks/useJobs.js';
import { useUpdateEntry, useDeleteEntry } from '../../hooks/useTimesheet.js';
import { useError } from '../shared/ErrorBanner.jsx';
import { toLocalDateTimeInput, fromLocalDateTimeInput } from '../../lib/format.js';

export default function EntryEditModal({ entry, onClose }) {
  const [inAt,  setInAt]  = useState(toLocalDateTimeInput(entry.clock_in_at));
  const [outAt, setOutAt] = useState(entry.clock_out_at ? toLocalDateTimeInput(entry.clock_out_at) : '');
  const [jobId, setJobId] = useState(entry.job_id || '');
  const [notes, setNotes] = useState(entry.notes || '');
  const [reason, setReason] = useState('');

  const { data: jobs = [] } = useJobs('all');
  const update = useUpdateEntry();
  const del = useDeleteEntry();
  const { show } = useError();

  const save = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { show('Reason required'); return; }
    try {
      await update.mutateAsync({
        id: entry.id,
        clock_in_at: fromLocalDateTimeInput(inAt),
        clock_out_at: outAt ? fromLocalDateTimeInput(outAt) : null,
        job_id: jobId || null,
        notes: notes || null,
        reason: reason.trim(),
      });
      onClose();
    } catch (err) { show(err.message); }
  };

  const remove = async () => {
    if (!reason.trim()) { show('Reason required'); return; }
    if (!confirm('Delete this entry permanently?')) return;
    try {
      await del.mutateAsync({ id: entry.id, reason: reason.trim() });
      onClose();
    } catch (err) { show(err.message); }
  };

  const busy = update.isPending || del.isPending;

  return (
    <Modal onClose={onClose} title={`Edit entry — ${entry.user_name}`}>
      <form onSubmit={save}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="field">
            <label className="field-label">Clock in</label>
            <input className="field-input" type="datetime-local" value={inAt}
              onChange={e => setInAt(e.target.value)} required />
          </div>
          <div className="field">
            <label className="field-label">Clock out</label>
            <input className="field-input" type="datetime-local" value={outAt}
              onChange={e => setOutAt(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label className="field-label">Job</label>
          <select className="field-select" value={jobId} onChange={e => setJobId(e.target.value)}>
            <option value="">— none —</option>
            {jobs.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="field-label">Notes</label>
          <textarea className="field-textarea" value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        <div className="field">
          <label className="field-label">Reason for change (required)</label>
          <input className="field-input" value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. forgot to clock out, off-by-one shift, …" required />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-sm primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button className="btn-sm" type="button" onClick={remove} disabled={busy}>
            Delete entry
          </button>
        </div>
      </form>
    </Modal>
  );
}
